import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  Button,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import AddBrand from "./AddBrand";
import {
  getAllBrands,
  deleteBrand,
  createBrand,
  updateBrand,
} from "../../service/brandService";
import { useSnackbar } from "../../App";
import Color from "../../components/Color";

const Brands = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { showSnackbar } = useSnackbar();

  // States
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [loading, setLoading] = useState(false);
  const [searchBrand, setSearchBrand] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ id: null, name: "", description: "" });
  const [sortModel, setSortModel] = useState([{ field: "id", sort: "desc" }]);

  const menuOpen = Boolean(anchorEl);

  // Open Menu
  const handleMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Fetch Brands with server-side pagination and sorting
  const fetchBrands = useCallback(async () => {
    setLoading(true);
    try {
      const page = paginationModel.page + 1;
      const limit = paginationModel.pageSize;
      const sortField = sortModel[0]?.field || "id";
      const sortOrder = sortModel[0]?.sort || "desc";

      const res = await getAllBrands(
        page,
        limit,
        debouncedSearch || "",
        sortField,
        sortOrder
      );

      const data = res?.data?.data;
      if (Array.isArray(data?.data)) {
        setRows(data.data);
        setRowCount(data.total);
      } else {
        setRows([]);
        showSnackbar("Unexpected data format!", "error");
      }
    } catch (error) {
      showSnackbar("Failed to fetch brands!", "error");
    }
    setLoading(false);
  }, [paginationModel, debouncedSearch, sortModel, showSnackbar]);

  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
      setDebouncedSearch(searchBrand);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchBrand]);

  // Handle delete click
  const handleDelete = () => {
    if (!selectedRow) {
      showSnackbar("No brand selected!", "error");
      return;
    }
    setDeleteDialogOpen(true);
    setAnchorEl(null); 
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    if (!selectedRow?.id) {
      showSnackbar("No brand ID found to delete!", "error");
      setDeleteDialogOpen(false);
      return;
    }

    try {
      await deleteBrand(selectedRow.id);
      showSnackbar("Brand deleted successfully!", "success");
      fetchBrands();
    } catch (error) {
      showSnackbar(error?.response?.data?.message || "Failed to delete brand!", "error");
    } finally {
      setDeleteDialogOpen(false);
      setSelectedRow(null);
    }
  };

  // Handle cancel delete
  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setSelectedRow(null);
  };

  // Handle Edit
  const handleEdit = (brand) => {
    setFormData({
      id: brand?.id,
      name: brand?.name || "",
      description: brand?.description || "",
    });
    setEditingId(brand?.id);
    setOpenModal(true);
    setAnchorEl(null);
  };

  // Handle Add/Edit Submit
  const handleSubmit = async (errorsCallback) => {
    try {
      const payload = { name: formData.name, description: formData.description };
      if (editingId) {
        await updateBrand(editingId, payload);
        showSnackbar("Brand updated successfully!", "success");
      } else {
        await createBrand(payload);
        showSnackbar("Brand added successfully!", "success");
      }
      setOpenModal(false);
      setEditingId(null);
      setFormData({ id: null, name: "", description: "" });
      fetchBrands();
    } catch (error) {
      if (error.response?.data?.errors) {
        const apiErrors = error.response.data.errors;
        errorsCallback(apiErrors); 
       
        const errorMessage =
          apiErrors.name?.[0] === "The name has already been taken."
            ? "The brand name is already taken. Please use a different name."
            : "Validation failed. Please check the form.";
        showSnackbar(errorMessage, "error");
      } else {
        showSnackbar(error?.response?.data?.message || "Something went wrong!", "error");
      }
    }
  };

  // DataGrid Columns
  const columns = [
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      minWidth: isMobile ? 80 : 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          onClick={(e) => handleMenuOpen(e, params.row)}
          sx={{
            backgroundColor: "rgba(0,0,0,0.04)",
            borderRadius: 1.5,
            padding: isMobile ? "10px" : "8px",
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.08)",
            },
          }}
        >
          <MoreVertIcon sx={{ fontSize: isMobile ? 18 : 16 }} />
        </IconButton>
      ),
    },
    { field: "name", headerName: "Brand Name", flex: 1, minWidth: isMobile ? 120 : 180 },
    { field: "description", headerName: "Description", flex: 2, minWidth: 200 },
  ];

  return (
    <Box sx={{ padding: isMobile ? 1 : 0, mb: { xs: 0 } }}>
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        justifyContent="space-between"
        alignItems={isMobile ? "stretch" : "center"}
        flexWrap="wrap"
        gap={isMobile ? 1 : 2}
        mt={2}
      >
        <TextField
          value={searchBrand}
          onChange={(e) => setSearchBrand(e.target.value)}
          placeholder="Search brand name"
          variant="outlined"
          InputLabelProps={{ shrink: false }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: isMobile ? 18 : 20 }} />
              </InputAdornment>
            ),
            endAdornment: searchBrand && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSearchBrand("");
                    setDebouncedSearch("");
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                  }}
                >
                  ✖
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiInputLabel-root": { display: "none" },
            "& .MuiOutlinedInput-root": {
              "&.Mui-focused fieldset": { borderColor: "#7C0BCC" },
              height: isMobile ? "34px" : "37.69px",
              fontSize: isMobile ? "0.85rem" : "1rem",
            },
            width: isMobile ? "100%" : 250,
            mb: isMobile ? 0 : 0,
          }}
        />
        <Button
          sx={{
            background: Color.primary.main,
            color: "white",
            height: isMobile ? "34px" : "37.69px",
            width: isMobile ? "100%" : 150,
            fontSize: isMobile ? "0.85rem" : "1rem",
            mt: isMobile ? 1 : 0,
            "&:hover": {
              background: Color.primary.dark,
            },
          }}
          onClick={() => {
            setEditingId(null);
            setFormData({ id: null, name: "", description: "" });
            setOpenModal(true);
          }}
          startIcon={<AddIcon />}
        >
          Add Brand
        </Button>
      </Box>

      <Box sx={{ width: "100%", mt: isMobile ? 1 : 2 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortModel={sortModel}
          onSortModelChange={(model) => setSortModel(model)}
          sortingMode="server"
          rowCount={rowCount}
          paginationMode="server"
          getRowId={(row) => row.id}
          rowHeight={isMobile ? 48 : 56}
          loading={loading}
          disableRowSelectionOnClick
          autoHeight
          sx={{
            fontFamily: '"Poppins", sans-serif',
            borderRadius: "10px",
            "& .MuiDataGrid-columnHeaderTitle": {
              fontSize: isMobile ? "14px" : "18px",
              fontWeight: 500,
              pl: 1,
            },
            "& .MuiDataGrid-cell": {
              fontSize: isMobile ? "12px" : "14px",
              pl: 2,
            },
            "& .MuiDataGrid-row:nth-of-type(even)": {
              backgroundColor: "#fff",
            },
            "& .MuiDataGrid-row:nth-of-type(odd)": {
              backgroundColor: "#f4f4f4",
            },
            "& .MuiLinearProgress-bar": {
              backgroundColor: Color.primary.main,
            },
            "& .MuiLinearProgress-colorPrimary": {
              backgroundColor: Color.secondary.main,
            },
            overflowX: isMobile ? "hidden" : "auto",
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
              outline: "none",
              border: "2px solid",
              borderColor: Color.primary.main,
              borderRadius: "4px",
            },
            "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
              outline: "none",
              border: "2px solid",
              borderColor: Color.primary.main,
              borderRadius: "4px",
            },
          }}
        />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        PaperProps={{
          style: {
            maxHeight: 200,
            width: isMobile ? "90vw" : 200,
          },
        }}
      >
        <MenuItem
          onClick={() => handleEdit(selectedRow)}
          sx={{ fontSize: isMobile ? "0.85rem" : "1rem", padding: isMobile ? "10px" : "8px" }}
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem
          onClick={handleDelete}
          sx={{ fontSize: isMobile ? "0.85rem" : "1rem", padding: isMobile ? "10px" : "8px" }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1, color: "error.main" }} /> Delete
        </MenuItem>
      </Menu>

      <AddBrand
        open={openModal}
        onClose={() => {
          setOpenModal(false);
          setEditingId(null);
          setFormData({ id: null, name: "", description: "" });
        }}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            width: isMobile ? "90vw" : "auto",
            m: isMobile ? 1 : 2,
          },
        }}
      >
        <DialogTitle sx={{ fontSize: isMobile ? "1rem" : "1.25rem" }}>
          Delete Confirmation
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: isMobile ? "0.85rem" : "1rem" }}>
            Are you sure you want to delete the brand <strong>{selectedRow?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            mr: 2,
            mb: 2,
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 1 : 0,
          }}
        >
          <Button
            onClick={handleCancelDelete}
            sx={{
              border: "1px solid",
              borderColor: Color.secondary.main,
              color: Color.label.main,
              width: isMobile ? "100%" : "auto",
              fontSize: isMobile ? "0.85rem" : "1rem",
              padding: isMobile ? "8px" : "6px 16px",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            sx={{
              background: Color.primary.main,
              color: "white",
              width: isMobile ? "100%" : "auto",
              fontSize: isMobile ? "0.85rem" : "1rem",
              padding: isMobile ? "8px" : "6px 16px",
              "&:hover": {
                background: Color.primary.dark,
              },
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Brands;
